import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import microsoftTeamsIcon from '~/assets/public/microsoft-teams.svg';
import { Avatar, Button, Buttons, Icon, SingleSelect } from '~/components';
import { useApi, useSession } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { colors, weights } from '~/styles';
import { ErrorPage, PageLoader } from '../../pages';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 1.5rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 30rem;
  padding: 2rem;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey10};
  border-radius: 0.625rem;
`;

const Pairing = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Tile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey10};
  border-radius: 0.75rem;
  overflow: hidden;
`;

const TileImage = styled.img`
  width: 2.25rem;
  height: 2.25rem;
  object-fit: contain;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: ${weights.light};
  line-height: 1.3;
  text-align: center;
`;

const Strong = styled.span`
  font-weight: ${weights.bold};
`;

const Subtitle = styled.div`
  margin-top: 0.5rem;
  color: ${colors.grey55};
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: center;
`;

const Section = styled.div`
  margin-top: 2rem;
`;

const SectionLabel = styled.div`
  margin-bottom: 0.5rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  letter-spacing: 0.05rem;
  text-transform: uppercase;
`;

const Account = styled.div`
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
`;

const AccountDetail = styled.div`
  color: ${colors.grey55};
  font-size: 0.8125rem;
`;

const Scope = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const ScopeIcon = styled(Icon)`
  flex-shrink: 0;
  margin-top: 0.2rem;
  margin-right: 0.625rem;
  color: ${colors.primary};
`;

const WorkspaceOption = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;

  span {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const WorkspaceLogo = styled.img`
  width: 1.25rem;
  height: 1.25rem;
  object-fit: contain;
  flex-shrink: 0;
`;

const Message = styled.p`
  margin-top: 1rem;
  color: ${colors.danger};
  font-size: 0.875rem;
`;

const Hint = styled.p`
  margin-top: 1.5rem;
  color: ${colors.grey55};
  font-size: 0.8125rem;
  text-align: center;
`;

const Actions = styled.div`
  margin-top: 2rem;
`;

function workspaceDisplay(workspace) {
  if (!workspace) return null;

  return (
    <WorkspaceOption>
      {workspace.imageUrl ? (
        <WorkspaceLogo src={workspace.imageUrl} alt="" />
      ) : (
        <Avatar value={{ ...workspace, name: workspace.name?.charAt(0) }} size={20} isCircle hasBackground />
      )}
      <span>{workspace.name}</span>
    </WorkspaceOption>
  );
}

function WorkspaceTile({ workspace }) {
  if (workspace?.imageUrl) {
    return (
      <Tile>
        <TileImage src={workspace.imageUrl} alt={workspace.name} />
      </Tile>
    );
  }
  return (
    <Tile>
      <Avatar value={{ ...workspace, name: (workspace?.name ?? 'Ruddr').charAt(0) }} size={36} isCircle hasBackground />
    </Tile>
  );
}

function Header({ workspace, isSuccess }) {
  return (
    <Pairing>
      <Tile>
        <TileImage src={microsoftTeamsIcon} alt="Microsoft Teams" />
      </Tile>
      <Icon icon={isSuccess ? 'check-circle' : 'right-left'} color={isSuccess ? colors.success : colors.grey25} />
      <WorkspaceTile workspace={workspace} />
    </Pairing>
  );
}

function StatusCard({ icon, iconColor, title, children, actions, testId }) {
  return (
    <Wrapper>
      <Card data-testid={testId}>
        <Pairing>
          <Icon icon={icon} size="3x" color={iconColor} />
        </Pairing>
        <Title>{title}</Title>
        {children}
        {actions && (
          <Actions>
            <Buttons align="center">{actions}</Buttons>
          </Actions>
        )}
      </Card>
    </Wrapper>
  );
}

export default function MicrosoftTeamsLinkRequest() {
  useDocumentTitle('Microsoft Teams');

  const api = useApi();
  const history = useHistory();
  const location = useLocation();
  const { token } = useParams();
  const { isReady: isSessionReady, session } = useSession();

  const [request, setRequest] = useState();
  const [workspaceId, setWorkspaceId] = useState('');
  const [error, setError] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const [result, setResult] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isSessionReady) return;

    if (!session) {
      history.replace(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }

    (async () => {
      try {
        const { data } = await api.www.microsoftTeamsLinkRequests(token).get();
        setRequest(data);
        const preferred = data.workspaces.find((workspace) => !workspace.connects) ?? data.workspaces[0];
        if (preferred) setWorkspaceId(preferred.id);
      } catch (requestError) {
        setError(requestError);
      }
    })();
  }, [api, history, isSessionReady, location.pathname, session, token]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setErrorMessage();
    try {
      const { data } = await api.www.microsoftTeamsLinkRequests(token).confirm({ workspaceId });
      setResult(data);
    } catch ({ message }) {
      setErrorMessage(message || 'There was a problem completing your request. Please try again.');
      setIsSubmitting(false);
    }
  };

  const goToRuddr = <Button onClick={() => history.push('/app')}>Go to Ruddr</Button>;

  if (result) {
    const { workspace, connected } = result;
    const selected = request.workspaces.find((candidate) => candidate.id === workspace.id) ?? workspace;

    return (
      <Wrapper>
        <Card data-testid="ms-teams-link-result">
          <Header workspace={selected} isSuccess />
          <Title>
            {connected ? (
              <>
                Microsoft Teams is connected to <Strong>{workspace.name}</Strong>
              </>
            ) : (
              <>
                Your Microsoft Teams account is linked to <Strong>{workspace.name}</Strong>
              </>
            )}
          </Title>
          {connected && <Subtitle>Your Microsoft Teams account is linked too.</Subtitle>}

          <Actions>
            <Buttons align="center">
              <Button
                onClick={() =>
                  history.push(
                    connected ? `/app/${workspace.key}/settings/integrations/microsoft` : `/app/${workspace.key}`,
                  )
                }>
                Go to Ruddr
              </Button>
            </Buttons>
          </Actions>
          <Hint>You can close this tab.</Hint>
        </Card>
      </Wrapper>
    );
  }

  if (error?.status === 410) {
    return (
      <StatusCard
        testId="ms-teams-link-result"
        icon="check-circle"
        iconColor={colors.grey25}
        title="This link has already been used"
        actions={goToRuddr}>
        <Subtitle>Ask Ruddr in Microsoft Teams for a new one if you need to link again.</Subtitle>
      </StatusCard>
    );
  }
  if (error?.status === 404) {
    return (
      <StatusCard
        testId="ms-teams-link-result"
        icon="hourglass"
        iconColor={colors.grey25}
        title="This link has expired"
        actions={goToRuddr}>
        <Subtitle>Ask Ruddr in Microsoft Teams for a new one.</Subtitle>
      </StatusCard>
    );
  }
  if (error) return <ErrorPage publicSite />;
  if (!isSessionReady || !request) return <PageLoader />;

  const isConnect = request.kind === 'connect';
  const organization = request.tenantDomain ?? 'your organization';

  const otherTenant = (name) =>
    `${name} is already connected to another Microsoft 365 organization. A Ruddr workspace admin must remove that connection in Ruddr first.`;
  const outsidePreview = (name) => `${name} isn't part of the Microsoft 365 preview yet.`;

  if (request.workspaces.length === 0 && request.unavailableWorkspaces.length > 0) {
    return (
      <StatusCard
        testId="ms-teams-link-unavailable"
        icon="exclamation-triangle"
        iconColor={colors.warning}
        title="Already connected to another organization"
        actions={goToRuddr}>
        {request.unavailableWorkspaces.map((workspace) => (
          <Subtitle key={workspace.id}>{otherTenant(workspace.name)}</Subtitle>
        ))}
      </StatusCard>
    );
  }

  if (request.workspaces.length === 0 && request.outsidePreviewWorkspaces.length > 0) {
    return (
      <StatusCard
        testId="ms-teams-link-unavailable"
        icon="exclamation-triangle"
        iconColor={colors.warning}
        title="Microsoft 365 isn't available yet"
        actions={goToRuddr}>
        {request.outsidePreviewWorkspaces.map((workspace) => (
          <Subtitle key={workspace.id}>{outsidePreview(workspace.name)}</Subtitle>
        ))}
      </StatusCard>
    );
  }

  if (request.workspaces.length === 0 && request.alreadyLinked) {
    return (
      <StatusCard
        testId="ms-teams-link-unavailable"
        icon="check-circle"
        iconColor={colors.grey25}
        title="Your Microsoft Teams account is already linked to all your Ruddr workspaces"
        actions={goToRuddr}
      />
    );
  }

  if (request.workspaces.length === 0) {
    return (
      <StatusCard
        testId="ms-teams-link-unavailable"
        icon="exclamation-triangle"
        iconColor={colors.warning}
        title={isConnect ? 'Only a workspace admin can connect' : 'Your organization isn’t connected yet'}
        actions={goToRuddr}>
        <Subtitle>
          {isConnect
            ? 'Ask a workspace admin to connect your organization, then send Ruddr a message in Microsoft Teams.'
            : `Microsoft Teams isn't connected to your Ruddr workspace for ${organization} yet. A Ruddr workspace admin must connect the organization first.`}
        </Subtitle>
      </StatusCard>
    );
  }

  const selectedWorkspace = request.workspaces.find((workspace) => workspace.id === workspaceId);
  const connects = !isConnect && selectedWorkspace?.connects;

  return (
    <Wrapper>
      <Card>
        <Header workspace={selectedWorkspace} />
        <Title>
          {isConnect ? (
            <>
              Connect Microsoft Teams for <Strong>{organization}</Strong>
              {selectedWorkspace && (
                <>
                  {' '}
                  to workspace <Strong>{selectedWorkspace.name}</Strong>
                </>
              )}
            </>
          ) : (
            <>Link your Microsoft Teams account to Ruddr</>
          )}
        </Title>

        <Section>
          <SectionLabel>Microsoft Teams account</SectionLabel>
          <Account data-testid="ms-teams-link-account">
            <div>{request.displayName ?? request.userPrincipalName}</div>
            {request.displayName && request.userPrincipalName && (
              <AccountDetail>{request.userPrincipalName}</AccountDetail>
            )}
            {(isConnect || connects) && request.tenantDomain && (
              <AccountDetail>Organization: {request.tenantDomain}</AccountDetail>
            )}
          </Account>
        </Section>

        <Section>
          <SectionLabel>Workspace</SectionLabel>
          <SingleSelect
            name="workspace"
            value={workspaceId}
            disabled={request.workspaces.length === 1}
            valueRenderer={() => workspaceDisplay(selectedWorkspace)}
            onChange={(event) => setWorkspaceId(event.target.value)}>
            {request.workspaces.map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {workspaceDisplay({
                  ...workspace,
                  name: !isConnect && workspace.connects ? `${workspace.name} (not connected yet)` : workspace.name,
                })}
              </option>
            ))}
          </SingleSelect>
          {request.unavailableWorkspaces.map((workspace) => (
            <AccountDetail key={workspace.id} data-testid="ms-teams-link-elsewhere">
              {otherTenant(workspace.name)}
            </AccountDetail>
          ))}
          {request.outsidePreviewWorkspaces.map((workspace) => (
            <AccountDetail key={workspace.id} data-testid="ms-teams-link-outside-preview">
              {outsidePreview(workspace.name)}
            </AccountDetail>
          ))}
        </Section>

        {connects && (
          <Section>
            <Account data-testid="ms-teams-link-connect-note">
              {selectedWorkspace.name} isn't connected to {organization} yet. Because you're a workspace admin, linking
              your account also connects the workspace, so its other members can link their own accounts.
            </Account>
          </Section>
        )}

        {selectedWorkspace && (
          <Section>
            <SectionLabel>What this does</SectionLabel>
            {isConnect || connects ? (
              <>
                <Scope data-testid="ms-teams-link-connects">
                  <ScopeIcon icon="check" />
                  <span>
                    Connects Microsoft Teams for {organization} to {selectedWorkspace.name}, so its members can link
                    their accounts.
                  </span>
                </Scope>
                <Scope>
                  <ScopeIcon icon="check" />
                  <span>Links your Microsoft Teams account to {selectedWorkspace.name}.</span>
                </Scope>
              </>
            ) : (
              <Scope data-testid="ms-teams-link-links">
                <ScopeIcon icon="check" />
                <span>Links your Microsoft Teams account to {selectedWorkspace.name}.</span>
              </Scope>
            )}
          </Section>
        )}

        {errorMessage && <Message>{errorMessage}</Message>}

        <Actions>
          <Buttons align="right">
            <Button disabled={!workspaceId || isSubmitting} isLoading={isSubmitting} onClick={handleConfirm}>
              {isConnect ? 'Connect' : 'Link'}
            </Button>
          </Buttons>
        </Actions>
      </Card>
    </Wrapper>
  );
}
